import {
  Add,
  DeleteOutline,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { Box, Collapse, Divider, IconButton, Stack } from '@mui/material';
import TextField from '@mui/material/TextField';
import { useCallback, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from '../../../hooks/useTranslation';
import StyledFormItem from '../../../styles/StyledFormItem';
import Button from '../../shared/Button/Button';
import DropDown from '../../shared/DropDown/DropDown';
import Input from '../../shared/Input/Input';
import Switch from '../../shared/Switch/Switch';
import Text from '../../shared/Text/Text';
import ToolTip from '../../shared/ToolTip/ToolTip';
import WorkflowStepLabel from '../../shared/WorkflowStepLabel/WorkflowStepLabel';
import AnalyticsFieldsSection from './AnalyticsFieldsSection';

const clampSamplingRate = (rawValue: string): number | undefined => {
  if (rawValue === '') return undefined;
  const parsed = Number(rawValue);
  if (Number.isNaN(parsed)) return undefined;
  return Math.min(100, Math.max(0, parsed));
};

interface CategoryError {
  name?: {
    message?: string;
    type?: string;
  };
  description?: {
    message?: string;
    type?: string;
  };
  email?: {
    message?: string;
    type?: string;
  };
}

interface AgentAnalyticsSettingsStepProps {
  stepLabel?: string;
  label?: string;
  description?: string;
  /**
   * Drives interpolated tooltip strings ('chat' | 'call').
   * Defaults to 'chat'.
   */
  agentType?: 'chat' | 'call';
  /**
   * The react-hook-form field array name for the categories list.
   * Defaults to 'analytics_categories'.
   */
  fieldArrayName?: string;
  /**
   * The form field name for the summaries language dropdown.
   * Defaults to 'analytics_language'.
   */
  languageFieldName?: string;
  /**
   * The key used to look up per-category validation errors in formState.errors.
   * Defaults to the value of `fieldArrayName`.
   */
  categoriesErrorKey?: string;
  /**
   * Maximum number of categories the user can add.
   * Defaults to 20.
   */
  maxCategories?: number;
  /**
   * Whether to show the email field per category.
   * Defaults to true — set to false for chat agents.
   */
  showEmailField?: boolean;
  /**
   * The form field name for the unknown category email.
   * When provided, a subtle email input is rendered for the "Unknown" catch-all category.
   * Only used for call agents.
   */
  unknownCategoryEmailFieldName?: string;
}

const AgentAnalyticsSettingsStep: React.FC<AgentAnalyticsSettingsStepProps> = ({
  stepLabel,
  label,
  description,
  agentType = 'chat',
  fieldArrayName = 'analytics_categories',
  languageFieldName = 'analytics_language',
  categoriesErrorKey,
  maxCategories = 75,
  showEmailField = true,
  unknownCategoryEmailFieldName,
}) => {
  const { t } = useTranslation();
  const [isUnknownEmailExpanded, setIsUnknownEmailExpanded] = useState(false);

  const resolvedErrorKey = categoriesErrorKey ?? fieldArrayName;

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const categoryErrors = (errors as unknown as Record<string, CategoryError[]>)[
    resolvedErrorKey
  ];

  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldArrayName,
  });

  const handleAdd = useCallback(() => {
    append(
      showEmailField
        ? { name: '', description: '', email: '' }
        : { name: '', description: '' }
    );
  }, [append, showEmailField]);

  const handleRemove = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove]
  );

  const languageOptions = [
    { label: t('English'), value: 'english' },
    { label: t('French'), value: 'french' },
    { label: t('Spanish'), value: 'spanish' },
    { label: t('German'), value: 'german' },
    { label: t('Italian'), value: 'italian' },
  ];

  return (
    <div>
      <WorkflowStepLabel
        stepLabel={stepLabel}
        label={label ?? t('Analytics Settings')}
        description={description}
      />

      <StyledFormItem>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Text type="label">
            {t('Summaries Language')}
            <ToolTip
              title={t(
                'The language used for generating {{agentType}} summaries',
                { agentType }
              )}
            />
          </Text>
        </Stack>
        <DropDown
          name={languageFieldName}
          options={languageOptions}
          required={true}
          fullWidth={true}
          withError={!!errors[languageFieldName]}
          helperText={
            !!errors[languageFieldName]
              ? t('Summaries language is required')
              : ''
          }
        />
      </StyledFormItem>

      {agentType === 'call' && (
        <StyledFormItem>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Switch name="auto_fix" label={t('Autofix')} />
            <ToolTip
              title={t(
                'Automatically detect and fix recurring issues in your agent based on call feedback'
              )}
            />
          </Stack>

          <Stack direction="row" alignItems="center" gap={0.5}>
            <Text type="label">
              {t('Feedback Sampling Rate (%)')}
              <ToolTip
                title={t(
                  'The percentage of calls sampled for automatic quality review'
                )}
              />
            </Text>
          </Stack>
          <Controller
            name="feedback_sampling_rate"
            control={control}
            render={({ field }) => (
              <TextField
                type="number"
                fullWidth
                value={field.value ?? ''}
                onChange={event =>
                  field.onChange(clampSamplingRate(event.target.value))
                }
                onBlur={field.onBlur}
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 5,
                  'aria-label': t('Feedback Sampling Rate (%)'),
                }}
              />
            )}
          />
        </StyledFormItem>
      )}

      <Box sx={{ marginTop: '1.5rem', marginBottom: '0.5rem' }}>
        <Text
          type="subtitle1"
          sx={{
            fontSize: '1.25rem !important',
            '@media (min-width: 769px)': { fontSize: '1.25rem !important' },
            '@media (min-width: 1441px)': { fontSize: '1.25rem !important' },
            fontWeight: '600 !important',
            color: 'var(--page-sub-header)',
          }}
        >
          {t('Categories')}
        </Text>
      </Box>

      {unknownCategoryEmailFieldName && (
        <Box
          sx={{
            marginTop: '0.75rem',
            border: '1px solid rgba(131, 131, 131, 0.3)',
            borderRadius: '4px',
            backgroundColor: '#fff',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            onClick={() => setIsUnknownEmailExpanded(prev => !prev)}
            sx={{
              padding: '0.75rem 1rem',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Text type="label" sx={{ margin: 0 }}>
                {t('Unknown Category Email')}
              </Text>
              <ToolTip
                title={t(
                  'Email address for conversations that cannot be matched to any category'
                )}
              />
            </Stack>
            {isUnknownEmailExpanded ? (
              <ExpandLess sx={{ color: 'var(--page-sub-header)' }} />
            ) : (
              <ExpandMore sx={{ color: 'var(--page-sub-header)' }} />
            )}
          </Stack>
          <Collapse in={isUnknownEmailExpanded}>
            <Box sx={{ padding: '0 1rem 0.75rem' }}>
              <Input
                name={unknownCategoryEmailFieldName}
                type="email"
                placeholder={'email@address.com'}
                sx={{
                  '& .MuiOutlinedInput-root': { backgroundColor: '#f9f9f9' },
                }}
              />
            </Box>
          </Collapse>
        </Box>
      )}

      {fields.map((field, index) => (
        <Box key={field.id} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Divider
            sx={{
              marginTop: '0.75rem',
              fontSize: '0.8rem',
              color: 'var(--page-sub-header)',
              '&::before, &::after': {
                borderColor: 'rgba(131, 131, 131, 0.3)',
              },
            }}
          >
            {index + 1}
          </Divider>
          {/* Row 1: Name + Email (if shown) + Delete button */}
          <Box
            sx={{
              display: 'flex',
              gap: '1rem',
              alignItems: 'flex-start',
            }}
          >
            <StyledFormItem flexGrow={1} width="auto">
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Text type="label">
                  {t('Category Name')}
                  <ToolTip
                    title={t('A short name for this {{agentType}} category', {
                      agentType,
                    })}
                  />
                </Text>
              </Stack>
              <Input
                name={`${fieldArrayName}.${index}.name`}
                maxLength={50}
                placeholder={t('Sales')}
                withError={!!categoryErrors?.[index]?.name}
                helperText={
                  categoryErrors?.[index]?.name?.message ||
                  (!!categoryErrors?.[index]?.name
                    ? t('Category name is required')
                    : '')
                }
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
              />
            </StyledFormItem>

            {showEmailField && (
              <StyledFormItem flexGrow={1} width="auto">
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <Text type="label">
                    {t('Email')}
                    <ToolTip
                      title={t('Contact email address for this category')}
                    />
                  </Text>
                </Stack>
                <Input
                  name={`${fieldArrayName}.${index}.email`}
                  type="email"
                  withError={!!categoryErrors?.[index]?.email}
                  helperText={categoryErrors?.[index]?.email?.message || ''}
                  placeholder={'email@address.com'}
                  sx={{
                    '& .MuiOutlinedInput-root': { backgroundColor: '#fff' },
                  }}
                />
              </StyledFormItem>
            )}

            <IconButton
              color="error"
              sx={{
                height: '56px',
                width: '56px',
                marginTop: '70px',
                backgroundColor: '#fff',
                borderRadius: '4px',
                border: '1px solid lightgrey',
                flexShrink: 0,
              }}
              type="button"
              onClick={() => handleRemove(index)}
            >
              <DeleteOutline />
            </IconButton>
          </Box>

          {/* Row 2: Description textarea — full width */}
          <StyledFormItem>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Text type="label">
                {t('Category Description')}
                <ToolTip title={t('Describe what this category represents')} />
              </Text>
            </Stack>
            <Input
              name={`${fieldArrayName}.${index}.description`}
              maxLength={200}
              multiline
              inputProps={{ style: { minHeight: '100px' } }}
              placeholder={t('User has made a sales related request')}
              withError={!!categoryErrors?.[index]?.description}
              helperText={
                categoryErrors?.[index]?.description?.message ||
                (!!categoryErrors?.[index]?.description
                  ? t('Description is required')
                  : '')
              }
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
            />
          </StyledFormItem>
        </Box>
      ))}

      <Box sx={{ marginTop: '1rem' }}>
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          size="small"
          onClick={handleAdd}
          disabled={fields.length >= maxCategories}
          style={{ backgroundColor: '#fff' }}
        >
          <Add sx={{ marginRight: '0.5rem' }} />
          {t('Add')}
        </Button>
      </Box>

      {agentType === 'call' && <AnalyticsFieldsSection />}
    </div>
  );
};

export default AgentAnalyticsSettingsStep;
